import React, { Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Container } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { polyfillCSS } from "../../../utils/css";
import clsx from "clsx";
import { PageSkeleton } from "../../../components/loading/PageSkeleton";
import { COACH_PROGRAMS_FOLDERS_ROUTE } from "../../../routes/routes";
import {
  ComponentTemplatePrivacyType,
  ComponentTemplateSort,
} from "@growth-machine-llc/stridist-api-client";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { TemplateListFilterContext } from "../../../contexts/TemplateLibraryFilterContext";
import { CoachComponentTemplateLibraryHeaderFilters } from "../../../components/component-templates-library/CoachComponentTemplateLibraryHeaderFilters";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(11),
    paddingLeft: polyfillCSS(
      `calc(${theme.spacing(4)} + var(--safe-area-inset-left)) !important`,
    ),
    [theme.breakpoints.down("lg")]: {
      paddingLeft: "20px !important",
      paddingRight: "20px !important",
    },
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
}));

export interface TemplateLibraryLocalStorageFilters {
  templatePrivacyType: ComponentTemplatePrivacyType;
}

const TEMPLATE_LIBRARY_FILTERS_KEY = "templateLibraryFilters";

const CoachComponentTemplateLibraryWrapper = () => {
  const location = useLocation();
  const s = useStyles();

  const defaultFilters = {
    query: "",
    sortKey: ComponentTemplateSort.NAME_ASC,
  };

  const defaultLocalStorageFilters = {
    templatePrivacyType: ComponentTemplatePrivacyType.ALL,
  };

  const [query, setQuery] = React.useState(defaultFilters.query);

  const [sortKey, setSortKey] = React.useState<ComponentTemplateSort>(
    defaultFilters.sortKey,
  );

  const [filters, setFilters] =
    useLocalStorage<TemplateLibraryLocalStorageFilters>(
      TEMPLATE_LIBRARY_FILTERS_KEY,
      defaultLocalStorageFilters,
    );

  const { templatePrivacyType } = filters;

  const updateFilters = (
    newFilters: Partial<TemplateLibraryLocalStorageFilters>,
  ) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const handleQueryChange = React.useCallback(
    (query: string) => {
      setQuery(query);
    },
    [setQuery],
  );

  const handleSortKeyChange = React.useCallback(
    (sortKey: ComponentTemplateSort) => {
      setSortKey(sortKey);
    },
    [setSortKey],
  );

  const handleTemplatePrivacyTypeChange = React.useCallback(
    (templatePrivacyType: ComponentTemplatePrivacyType) => {
      updateFilters({ templatePrivacyType });
    },
    [updateFilters],
  );

  React.useEffect(() => {
    setQuery(defaultFilters.query);
    setSortKey(defaultFilters.sortKey);
  }, [location.pathname]);

  const filterContextValue = {
    query,
    sortKey,
    templatePrivacyType,
    handleQueryChange,
    handleSortKeyChange,
    handleTemplatePrivacyTypeChange,
  };

  return (
    <TemplateListFilterContext.Provider value={filterContextValue}>
      <Container className={clsx(s.root)} maxWidth="llg">
        {location.pathname !== COACH_PROGRAMS_FOLDERS_ROUTE && (
          <CoachComponentTemplateLibraryHeaderFilters
            className={s.filters}
            query={query}
            onChangeQuery={handleQueryChange}
            templatePrivacyType={templatePrivacyType}
            onChangeTemplatePrivacyType={handleTemplatePrivacyTypeChange}
            sortKey={sortKey}
            onChangeSortKey={handleSortKeyChange}
            defaultValues={{ ...defaultLocalStorageFilters, ...defaultFilters }}
          />
        )}
        <Suspense fallback={<PageSkeleton fullWidth />}>
          <Outlet />
        </Suspense>
      </Container>
    </TemplateListFilterContext.Provider>
  );
};

export default CoachComponentTemplateLibraryWrapper;
